import { auth } from "~/setups/firebase";
import { GetterTree, ActionTree, MutationTree } from "vuex";
import { functions } from "~/setups/firebase";
import type { HttpsCallableResult } from "@firebase/functions-types";
import { waitForAuthUser } from "~/utils/auth";
import PermissionService from "~/services/PermissionService";
import {
  readAdminData,
  readAllClinics,
  readClinic,
  readFormConfig,
} from "~/services/firestoreReads";
import { Service } from "@ambii-com/core";

export const state = () => ({
  userId: null,
  userRole: null,
  clinicId: null,
  clinicName: null,
  formConfig: null,
  superAdmin: null,
  clinicList: null,
  algoliaApiKeyPromise: null as Promise<HttpsCallableResult>,
  formActive: null,
  appointmentActive: null,
  appointmentTrackingActive: null,
  calloutActive: null,
  homepageActive: null,
  ready: false,
  paymentAuthDegraded: false,
  bootstrapPromise: null,
});

type AuthState = ReturnType<typeof state>;

export const getters: GetterTree<AuthState, any> = {
  getUserId: (state) => state.userId,
  isLoggedIn: (state) => !!state.userId,
  getClinicId: (state) => state.clinicId,
  getClinicName: (state) => state.clinicName,
  getFormConfig: (state) => state.formConfig,
  checkSuperadmin: (state) => state.superAdmin,
  getClinicList: (state) => state.clinicList,
  getUserRole: (state) => state.userRole,
  getAlgoliaAPIKeyPromise: (state) => state.algoliaApiKeyPromise,
  getFormActive: (state) => state.formActive,
  getAppointmentActive: (state) => state.appointmentActive,
  getAppointmentTrackingActive: (state) => state.appointmentTrackingActive,
  getCalloutActive: (state) => state.calloutActive,
  getHomepageActive: (state) => state.homepageActive,
  ready: (state) => state.ready,
  paymentAuthDegraded: (state) => state.paymentAuthDegraded,
};

export const actions: ActionTree<AuthState, any> = {
  async bootstrap({ state, commit, dispatch, getters}, app) {
    if (state.ready && state.userId && auth.currentUser && state.userId === auth.currentUser.uid) {
      return auth.currentUser
    }
    if (state.bootstrapPromise) return state.bootstrapPromise

    const p = (async () => {
      commit(
        "SET_PAYMENT_AUTH_DEGRADED",
        sessionStorage.getItem("ambiiPaymentAuthDegraded") === "true"
      )
      const user: any = auth.currentUser || (await waitForAuthUser(auth))
      if (!user) {
        commit("SET_READY", true)
        commit("SET_BOOTSTRAP_PROMISE", null)
        return null
      }

      await dispatch("setUser", user)

      const superadminCheckResponse = await PermissionService.getPermissionCheck(user.uid).catch(err => err.response)
      const isSuper = superadminCheckResponse?.data?.data?.user_type === app.$enums.UserType.SUPERADMIN

      if (isSuper) {
        localStorage.setItem("superadminEmail", user.email)
        await dispatch("setSuperadmin", true)
        await dispatch("setClinicList")
        await dispatch("setClinicName", null)
        await dispatch("setClinicId", null)
        await dispatch("setUserRole", null)
        await dispatch("setFormConfig", null)
        await dispatch("setFormActive", null)
        await dispatch("setAppointmentActive", null)
        await dispatch("setAppointmentTrackingActive", null)
        await dispatch("setCalloutActive", null)
        await dispatch("setHomepageActive", null)
      } else {
        await dispatch("setSuperadmin", false)

        const adminUser = await readAdminData(user.uid)
        await dispatch("setClinicId", adminUser.clinicId)
        await dispatch("setUserRole", adminUser.role)

        const clinic = await readClinic(adminUser.clinicId as string)
        await dispatch("setClinicName", clinic.name)
        await dispatch("setFormActive", clinic.activeServices.includes(Service.FORM))
        await dispatch("setAppointmentActive", clinic.activeServices.includes(Service.APPOINTMENT))
        await dispatch("setAppointmentTrackingActive", clinic.activeServices.includes(Service.APPOINTMENT_TRACKING))
        await dispatch("setCalloutActive", clinic.activeServices.includes(Service.CALLOUT))
        await dispatch("setHomepageActive", clinic.activeServices.includes("HOMEPAGE"))

        const formConfig = await readFormConfig(clinic.formConfigId)
        await dispatch("setFormConfig", formConfig)
      }

      if (!getters.getAlgoliaAPIKeyPromise) {
        await dispatch("setAlgoliaAPIKeyPromise")
      }

      commit("SET_READY", true)
      commit("SET_BOOTSTRAP_PROMISE", null)
      return user
    })()

    commit("SET_BOOTSTRAP_PROMISE", p)
    return p
  },
  setUser({ commit }, user) {
    commit("SET_USER", user);
  },
  // setUserRole({ commit }, role) {
  //     commit("SET_USER_ROLE", role)
  // },
  logout({ commit }) {
    return auth.signOut().then(() => {
      sessionStorage.removeItem("ambiiPaymentJwt");
      sessionStorage.removeItem("ambiiPaymentAuthDegraded");
      commit("SET_PAYMENT_AUTH_DEGRADED", false);
      localStorage.removeItem("superadminEmail");
      localStorage.removeItem("clinicId");
      commit("LOGOUT");
    });
  },
  setClinicId({ commit }, clinicId) {
    commit("SET_CLINICID", clinicId);
  },
  setClinicName({ commit }, clinicName) {
    commit("SET_CLINICNAME", clinicName);
  },
  setFormConfig({ commit }, formConfig) {
    commit("SET_FORMCONFIG", formConfig);
  },
  setSuperadmin({ commit }, superadmin) {
    commit("SET_SUPERADMIN", superadmin);
  },
  setUserRole({ commit }, userRole) {
    commit("SET_USERROLE", userRole);
  },
  setFormActive({ commit }, formActive) {
    commit("SET_FORMACTIVE", formActive);
  },
  setAppointmentActive({ commit }, appointmentActive) {
    commit("SET_APPOINTMENTACTIVE", appointmentActive);
  },
  setAppointmentTrackingActive({ commit }, appointmentTrackingActive) {
    commit("SET_APPOINTMENTTRACKINGACTIVE", appointmentTrackingActive);
  },
  setCalloutActive({ commit }, calloutActive) {
    commit("SET_CALLOUTACTIVE", calloutActive);
  },
  setHomepageActive({ commit }, homepageActive) {
    commit("SET_HOMEPAGEACTIVE", homepageActive);
  },
  async setClinicList({ commit }) {
    const clinicList = await readAllClinics();
    commit("SET_CLINICLIST", clinicList);
  },
  async setAlgoliaAPIKeyPromise({ state, commit }) {
    // currently no expiration for key
    const result: Promise<HttpsCallableResult> = functions.httpsCallable(
      "generateSecuredAPIKey"
    )({
      clinicId: state.clinicId,
    });
    commit("SET_ALGOLIA_API_KEY_PROMISE", result);
  },
};

export const mutations: MutationTree<AuthState> = {
  SET_USER(state, user) {
    state.userId = user.uid;
  },
  SET_USERROLE(state, role) {
    state.userRole = role;
  },
  LOGOUT(state) {
    state.clinicId = state.userId = state.clinicName = null;
    state.superAdmin = null;
    state.clinicList = null;
    state.userRole = null;
    state.formConfig = null;
    state.formActive = null;
    state.appointmentActive = null;
    state.appointmentTrackingActive = null;
    state.calloutActive = null;
    state.homepageActive = null;
    state.algoliaApiKeyPromise = null;
    state.ready = false;
    state.bootstrapPromise = null;
  },
  SET_CLINICID(state, clinicId) {
    state.clinicId = clinicId;
  },
  SET_CLINICNAME(state, clinicName) {
    state.clinicName = clinicName;
  },
  SET_FORMCONFIG(state, formConfig) {
    state.formConfig = formConfig;
  },
  SET_SUPERADMIN(state, superAdmin) {
    state.superAdmin = superAdmin;
  },
  SET_CLINICLIST(state, clinicList) {
    state.clinicList = clinicList;
  },
  SET_ALGOLIA_API_KEY_PROMISE(
    state,
    httpCallableResult: Promise<HttpsCallableResult>
  ) {
    state.algoliaApiKeyPromise = httpCallableResult;
  },
  SET_FORMACTIVE(state, formActive) {
    state.formActive = formActive;
  },
  SET_APPOINTMENTACTIVE(state, appointmentActive) {
    state.appointmentActive = appointmentActive;
  },
  SET_APPOINTMENTTRACKINGACTIVE(state, appointmentTrackingActive) {
    state.appointmentTrackingActive = appointmentTrackingActive;
  },
  SET_CALLOUTACTIVE(state, calloutActive) {
    state.calloutActive = calloutActive;
  },
  SET_HOMEPAGEACTIVE(state, homepageActive) {
    state.homepageActive = homepageActive;
  },
  SET_READY(state, ready) { state.ready = ready },
  SET_PAYMENT_AUTH_DEGRADED(state, degraded) { state.paymentAuthDegraded = degraded },
  SET_BOOTSTRAP_PROMISE(state, p) { state.bootstrapPromise = p },
};
